import { Pagination, IconButton } from "@mui/material";
import { DataGrid, Toolbar } from "devextreme-react";
import { Scrolling, FilterRow, HeaderFilter, SearchPanel, Column, Item, LoadPanel, Paging } from "devextreme-react/data-grid";
import { dataGridConfig, defaultPageSize } from "../../constants";
import { calculateGridHeight, convertUTCTimeToLocal } from "../../util/Utils";
import { useEffect, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import { UsersEntityMappingDTO } from "../../api-client-nswag/taxportal-client";
import { userAssignmentClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import { isEmpty, isNull } from "lodash";
import { SnackbarMessage, useSnackbar } from "../../hooks";
import UserEntityToggle, { getUserEntity } from "../../cui/commonComponents/UserEntityToggle";
import { DialogBox } from "../CreateSites/DialogBox";
import { UserHistory } from "./UserHistory";
import ArticleIcon from '@mui/icons-material/Article';
import { UserAssignmentHistoryResponse } from "../../api-client-nswag/userassignment-client";
import "../custompage.scss";

type DatagridRefType = DataGrid;

export interface usersInfo {
    pending: boolean
    data: UsersEntityMappingDTO[] | undefined;
    totalCount: number | undefined;
}

export const UserAssignments = () => {
    const dataGridRef = useRef<DatagridRefType>(null);
    const [pageNumber, setpageNumber] = useState(1);
    const appName = useRecoilValue(getUserEntity);
    var siteID = parseInt(sessionStorage.getItem("siteId")!);
    const [data, setData] = useState<usersInfo>({ pending: true, data: [], totalCount: 0 })
    const [searchTerm, setSearchTerm] = useState<string | null | undefined>(null);
    const { showSnackbar, showSuccessSnackbar } = useSnackbar();
    const [userId, setUserId] = useState<string>();
    const [isDisplay, setIsDiplay] = useState<boolean>(false);

    const fetchData = async () => {
        setData(prev => ({ ...prev, pending: true, data: [] }));
        try {
            const startIndex = pageNumber === 1 ? 0 : (pageNumber - 1) * defaultPageSize;
            const userAssignmentData: UserAssignmentHistoryResponse = await userAssignmentClientApi.userAssignment_UserAssignmentHistory(siteID, null, null, startIndex, defaultPageSize);
            setData({ pending: false, data: userAssignmentData?.data?.userAssignmentHistory, totalCount: userAssignmentData?.data?.totalCount })
        }
        catch (error) {
            setData(prev => ({ ...prev, pending: false, data: [] }));
            showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        }
    }

    const fetchDataBySearch = async () => {
        setData(prev => ({ ...prev, pending: true }));
        try {
            const userAssignmentData: UserAssignmentHistoryResponse = await userAssignmentClientApi.userAssignment_UserAssignmentHistory(siteID, searchTerm);
            setData({ pending: false, data: userAssignmentData?.data?.userAssignmentHistory, totalCount: userAssignmentData?.data?.totalCount })
        }
        catch (error) {
            setData(prev => ({ ...prev, pending: false }));
            showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        }
    }

    useEffect(() => {
        if (!isNull(searchTerm) && !isEmpty(searchTerm)) {
            fetchDataBySearch();
        }
    }, [appName, searchTerm]);

    useEffect(() => {
        if (searchTerm === null || searchTerm?.length === 0) {
            fetchData()
        }
    }, [pageNumber, appName, searchTerm]);

    const refreshButtonOptions = {
        icon: "refresh",
        onClick: async () => {
            setSearchTerm(null);
            if (dataGridRef.current) {
                dataGridRef.current.instance.clearFilter();
            }
            fetchData()
        },
    };

    const cellRenderForDate = (e: any) => {
        return <div>{convertUTCTimeToLocal(e.data.lastActivityDate)}</div>;
    };

    const cellRenderUserName = (e: any) => {
        return e.data.isActive == true ? <div>{e.data.userName}</div> : <div style={{ color: 'red' }}>{e.data.userName + '(User is not present in the site)'}</div>
    }

    const handleSeeDetailsButton = (userId: string) => {
        setUserId(userId)
        setIsDiplay(true)
    }

    const handleClose = () => {
        setIsDiplay(false)
    }

    const cellRenderActions = (e: any) => {
        return <IconButton
            className="dx-datagrid dx-link"
            aria-label="See Details"
            title="See Details"
            onClick={() => handleSeeDetailsButton(e.data.userId)}
            sx={{
                padding: '2px',
                marginTop: '-5px',
                marginBottom: '-5px',
                fontSize: '16px',
            }}
        >
            <ArticleIcon />
        </IconButton>
    }

    return (
        <section className="hq-page custom-page-shell user-assignments-page">
            <div className="custom-page-content">
                <div className="user-assignments-toolbar custom-page-header">
                    <UserEntityToggle />
                </div>

            <DataGrid
                height={`calc(100vh - 150px)`}
                {...dataGridConfig}
                dataSource={data.data}
                noDataText={data.pending ? "Loading..." : "No data"}
                allowColumnResizing={true}
                ref={dataGridRef}
            >
                <Paging defaultPageSize={10} />
                <Scrolling mode="standard" />
                <LoadPanel enabled={false} />
                <FilterRow visible={true} />
                <HeaderFilter visible={true} />
                <SearchPanel
                    placeholder={"Global Search..."}
                    onTextChange={(e) => {
                        setSearchTerm(e);
                    }}
                    visible
                />
                <Toolbar>
                    <Item name="searchPanel" location="after" />
                    <Item widget="dxButton" options={refreshButtonOptions} name="alignRight" />
                </Toolbar>
                <Scrolling columnRenderingMode="virtual" />
                <Column alignment="left" dataField="userName" caption={"User Name"} cellRender={cellRenderUserName}></Column>
                <Column alignment="left" dataField="roleName" caption={"Role"}></Column>
                <Column alignment="left" dataField="lastActivity" caption={"Latest Activity"}></Column>
                <Column
                    cellRender={cellRenderForDate}
                    dataField="lastActivityDate"
                    caption={"Latest Activity Date"}
                />
                <Column
                    cellRender={cellRenderActions}
                    type="buttons"
                    caption="User Activities" />

            </DataGrid>
            {isEmpty(searchTerm) && <div className="user-assignments-pager">
                <Pagination
                    onChange={(e: any, pageNumber: any) => {
                        setpageNumber(pageNumber);
                    }}
                    count={Math.ceil((data.totalCount as number) / defaultPageSize)}
                    variant="outlined"
                    shape="rounded"
                />
            </div>}
            <DialogBox
                isDisplayModal={isDisplay}
                handleClose={handleClose}
                title={"User Activity Log"}
            >
                <UserHistory
                    userId={userId}
                    siteId={siteID}
                />
            </DialogBox>
            </div>
        </section>
    );
}

